import { Reducer, useCallback, useReducer, useRef } from "react";

import { reducer } from "./reducer";
import {
  HistorySetStateInput,
  HistoryState,
  HistoryStateAction,
  HistoryStateActionType,
  StateWithHistory,
} from "./types";

const getInitialState = <T>(initialCurrentState: T): HistoryState<T> => ({
  current: initialCurrentState,
  past: [],
  future: [],
});

export const useStateWithHistory = <T>(
  initialCurrentState: T
): [T, (payload: HistorySetStateInput<T>) => void, StateWithHistory<T>] => {
  // get the initial state including past and future
  const initialState = getInitialState(initialCurrentState);

  const initialCurrentStateRef = useRef(initialCurrentState);

  // useReducer maintains current state value and past/future values
  const [state, dispatch] = useReducer<Reducer<HistoryState<T>, HistoryStateAction<T>>>(reducer, initialState);

  // dispatch undo
  const undo = () => {
    dispatch({ type: HistoryStateActionType.UNDO });
  };

  // dispatch redo
  const redo = () => {
    dispatch({ type: HistoryStateActionType.REDO });
  };

  // dispatch reset with initialCurrentState as default
  const reset = useCallback((payload: T = initialCurrentStateRef.current) => {
    dispatch({ type: HistoryStateActionType.RESET, payload });
  }, []);

  // dispatch update with payload
  const update = useCallback((payload: T) => {
    dispatch({ type: HistoryStateActionType.UPDATE, payload });
  }, []);

  // setState available externally, either as a value or callback function returning a value
  const setState = useCallback(
    (payload: T | ((prevState: T) => T)) =>
      payload instanceof Function ? update(payload(state.current)) : update(payload),
    [state, update]
  );

  return [state.current, setState, { past: state.past, future: state.future, undo, redo, reset }];
};
